import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Api from "./index.js";
import apiClient from "./axios";
import { QUERY_KEYS } from "./QueryKeys.js";
import { UNGROUPED } from "../constants";
import { translationServices } from "./useTranslationHooks.js";
import { DEFAULT_GIFT_CARD_SETTINGS } from "../containers/Restaurant/giftcard_settings/constants.js";

// temp change to utils
const handleApiError = (response) => {
  if (!response?.success) {
    throw new Error(response?.msg || "An unknown error occurred");
  }
  return response;
};

export const useGetRestaurantData = (restaurantId) => {
  return useQuery({
    queryKey: [QUERY_KEYS.restaurantData, restaurantId],
    queryFn: () => Api.getRestaurantData(restaurantId).then((res) => res.data),
    enabled: !!restaurantId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export const useUpdateRestaurantData = (successFn, errorFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await Api.updateRestaurant(data, data._id);
      return handleApiError(response);
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.restaurantData, variables._id] });
      successFn?.(result);
    },
    onError: errorFn,
  });
}

export const roleServices = {
  useGetAllRoles: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.Roles],
      queryFn: () => Api.getAllRoles(),
    });
  },
  useCreateRole: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (data) => {
        const response = await Api.createRole(data);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
  useUpdateRole: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async ({ id, data }) => {
        const response = await Api.updateRole(id, data);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
  useDeleteRole: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (id) => {
        const response = await Api.deleteRole(id);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
};

export const featureServices = {
  // Feature Groups
  useGetAllGroups: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.FeatureGroups],
      queryFn: () => Api.getAllFeatureGroups().then((res) => res.data),
      select: (data) => [
        ...data.groups,
        {
          _id: UNGROUPED,
          groupName: "Ungrouped",
          groupOrder: -1,
          features: data.ungrouped,
        },
      ],
    });
  },

  useCreateGroup: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) =>
        Api.createFeatureGroup(data).then((res) => res.data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },

  useUpdateGroup: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) => Api.updateFeatureGroup(data).then(() => data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },

  useDeleteGroup: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) => Api.deleteFeatureGroup(data).then(() => data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },

  // Features
  useGetAllFeatures: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.Features],
      queryFn: () => Api.getAllFeatures(),
    });
  },

  useCreateFeature: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) =>
        Api.createFeature(data).then((res) =>
          data._idGroup ? { ...res.data, _idGroup: data._idGroup } : res.data
        ),
      onSuccess: successFn,
      onError: errorFn,
    });
  },

  useUpdateFeature: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) => Api.updateFeature(data).then(() => data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },

  useDeleteFeature: (successFn, errorFn) => {
    return useMutation({
      mutationFn: (data) =>
        Api.deleteFeature(data.featureData._id).then(() => data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },
};

export const userServices = {
  useDeleteUser: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (userId) => {
        const response = await Api.deleteUser(userId);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
};

export const importExportFoodoJsonServices = {
  useImportFoodoJson: () => {
    return useMutation({
      mutationFn: async ({ restaurantId, data }) => {
        const res = await Api.importFoodoMenuJson(restaurantId, data);
        return handleApiError(res);
      },
    });
  },

  useExportFoodoJson: () => {
    return useMutation({
      mutationFn: async (restaurantId) => {
        const res = await Api.exportFoodoMenuJson(restaurantId);
        return handleApiError(res);
      },
    });
  },
};

export const restaurantCategoryService = {
  useGetRestaurantCategories: (successFn, errorFn) => {
    return useQuery({
      queryKey: [QUERY_KEYS.categories],
      queryFn: () => Api.getRestaurantCategoryList("all").then((res) => res.data),
      onSuccess: successFn,
      onError: errorFn,
    });
  },
  useCreateRestaurantCategory: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (data) => {
        const response = await Api.createRestaurantCategory(data);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn
    });
  },
  useUpdateRestaurantCategory: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (data) => {
        const response = await Api.updateRestaurantCategory(data);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn
    });
  },
  useDeleteRestaurantCategory: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (id) => {
        const response = await Api.deleteRestaurantCategory(id);
        return { response: handleApiError(response), _id: id };
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  }
}

export const tableBookingService = {
  useGetTableBookingSettings: (restaurantId) => {
    return useQuery({
      queryKey: [QUERY_KEYS.tableBookingOptions, restaurantId],
      queryFn: () => Api.getRestaurantData(restaurantId).then((res) => res.data?.tableBookingOptions || {}),
      enabled: !!restaurantId,
    });
  },
  useUpdateTableBookingSettings: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async ({ restaurantId, data }) => {
        const payload = JSON.stringify({ tableBookingOptions: data });
        const response = await Api.updateRestaurant(payload, restaurantId);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
}

export const tableBookingRestaurantSettingsService = {
  useGetTableBookingRestaurantSettings: (restaurantId) => {
    return useQuery({
      queryKey: [QUERY_KEYS.tableBookingRestaurantSettings, restaurantId],
      queryFn: () => Api.getTableBookingRestaurantSettings(restaurantId).then((res) => res.data || null),
      enabled: !!restaurantId,
    });
  },
  useCreateTableBookingRestaurantSettings: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async (data) => {
        const response = await Api.createTableBookingRestaurantSettings(data);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
}

export const giftCardSettingsService = {
  useGetGiftCardSettings: (restaurantId) => {
    return useQuery({
      queryKey: [QUERY_KEYS.giftCardSettings, restaurantId],
      queryFn: () => Api.getRestaurantData(restaurantId).then((res) => res.data?.giftCardOptions || DEFAULT_GIFT_CARD_SETTINGS.giftCardOptions),
      enabled: !!restaurantId,
    });
  },
  useUpdateGiftCardSettings: (successFn, errorFn) => {
    return useMutation({
      mutationFn: async ({ restaurantId, data }) => {
        const payload = JSON.stringify({ giftCardOptions: data });
        const response = await Api.updateRestaurant(payload, restaurantId);
        return handleApiError(response);
      },
      onSuccess: successFn,
      onError: errorFn,
    });
  },
}

export const countryServices = {
  useGetAllActiveCountries: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.activeCountries],
      queryFn: () => Api.getAllActiveCountries().then((res) => res.data || []),
    });
  },
  useGetCountryById: (id) => {
    return useQuery({
      queryKey: [QUERY_KEYS.country, id],
      queryFn: () => Api.getCountryById(id).then((res) => res.data),
      enabled: !!id,
    });
  },
  useGetCountries: (params = {}) => {
    return useQuery({
      queryKey: [QUERY_KEYS.countries, params],
      queryFn: () => Api.getCountries(params).then((res) => res.data),
    });
  },
}

export const commonCountryServices = {
  useGetCountryList: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.countryList],
      queryFn: async () => {
        const { data } = await apiClient.get("/countries/all");
        const rows = data?.data?.rows ?? [];
        // Treat an empty response as an error so React Query retries it
        if (!rows.length) throw new Error("No countries returned from /countries/all");
        return rows;
      },
      staleTime: 1000 * 60 * 30,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    });
  },
};

// Export translation services for easy access
export { translationServices };
